import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, Stack, useTheme, useMediaQuery } from "@mui/material";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";

/**
 * PatternPreview — clean rewrite.
 *
 * Design principles after the buggy v1:
 *   - No global pan / zoom. The view auto-fits to whatever it has to show
 *     (target + pattern overlay + every gun's current offset). Pinch/wheel are gone.
 *     This eliminates the entire class of "drag the background and the image flies away"
 *     bugs because there is no background drag handler at all.
 *   - Each gun marker handles its own pointer-events with its own pointer capture.
 *     Touch and mouse use the exact same code path via Pointer Events.
 *   - Constraints (circle radius / linear axis / snap) apply during drag so the
 *     marker visibly snaps to its valid position.
 *   - Tap on background does nothing destructive; it only deselects.
 *   - Reset button returns every gun to (0, 0).
 *
 * Props (unchanged from v1):
 *   guns           Array<{ gun_number }>      committed guns to draw
 *   points         { [gunId]: {dx, dy} }      current offsets in meters (e/n)
 *   setPoints      Setter                     write-back when a gun is dragged
 *   mode           "circle" | "linear" | "free"
 *   enabled        boolean                    if false the preview is read-only
 *   radiusMeters   number                     for circle mode
 *   bearingMils    number                     for linear mode (axis direction)
 *   spacingMeters  number                     for linear mode tick spacing
 *   snapToTicks    boolean                    linear: snap to spacing ticks along axis
 *   ringStepMeters number                     polar grid minor ring step
 *   radialStep     number (mils)              polar grid minor radial step
 *   showLabels     boolean                    draw range/azimuth labels on grid
 *   snapEnabled    boolean                    free-mode snap to (snapMeters)
 *   snapMeters     number                     free-mode snap grid size
 */

const milsToRad = (m) => (m * 2 * Math.PI) / 6400;
const radToMils = (r) => ((r * 6400) / (2 * Math.PI) + 6400) % 6400;
const azimuthMilsFromVector = (dx, dy) => radToMils(Math.atan2(dx, dy));
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const snapScalar = (v, step) => {
  const s = Math.max(1, Number(step) || 1);
  return Math.round(v / s) * s;
};

const PatternPreview = memo(function PatternPreview({
  guns,
  points,
  setPoints,
  mode,
  enabled,
  radiusMeters,
  bearingMils,
  spacingMeters,
  snapToTicks,
  ringStepMeters,
  radialStep,
  showLabels,
  snapEnabled,
  snapMeters,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ── Container sizing (responsive) ───────────────────────────────────────────
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(520);
  useEffect(() => {
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      setContainerWidth(Math.max(280, Math.floor(el.clientWidth)));
    };
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      if (containerRef.current) ro.observe(containerRef.current);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const size = Math.min(containerWidth, isMobile ? 380 : 520);
  const centerPx = size / 2;
  const PADDING_PX = 36; // empty room around the visible content

  // ── Theme ───────────────────────────────────────────────────────────────────
  const colors = useMemo(
    () => ({
      bg: isDark ? "#11161d" : "#ffffff",
      border: isDark ? "#2f3944" : "#cccccc",
      ringMinor: isDark ? "#202831" : "#f0f0f0",
      ringMajor: isDark ? "#3d4956" : "#bdbdbd",
      radialMinor: isDark ? "#1c232b" : "#f4f4f4",
      radialMajor: isDark ? "#3a4450" : "#cfcfcf",
      target: isDark ? "#e6edf3" : "#000000",
      targetLabel: isDark ? "#cfd6df" : "#222222",
      overlay: theme.palette.warning.main,
      gun: isDark ? "#f3f4f6" : "#0d1117",
      gunSelected: theme.palette.warning.main,
      gunLabel: isDark ? "#e5e7eb" : "#111111",
      gunSubLabel: isDark ? "#9ca3af" : "#444444",
      hudText: isDark ? "#dbe4ee" : "#333333",
      hudSubText: isDark ? "#94a3b8" : "#666666",
    }),
    [isDark, theme.palette.warning.main],
  );

  // ── Apply current pattern constraints to a raw (dx, dy) offset ──────────────
  const applyConstraint = (dx, dy, opts = {}) => {
    if (!enabled) return { dx, dy };

    if (mode === "circle") {
      const R = Number(radiusMeters) || 0;
      const r = Math.hypot(dx, dy);
      let out;
      if (r < 1e-6) out = { dx: R, dy: 0 };
      else out = { dx: (dx / r) * R, dy: (dy / r) * R };
      if (snapEnabled && !opts.noSnap) {
        out = { dx: snapScalar(out.dx, snapMeters), dy: snapScalar(out.dy, snapMeters) };
      }
      return out;
    }

    if (mode === "linear") {
      const a = milsToRad(Number(bearingMils) || 0);
      const ux = Math.sin(a);
      const uy = Math.cos(a);
      // project onto axis
      let t = dx * ux + dy * uy;
      if (snapToTicks) {
        const sp = Math.max(1, Number(spacingMeters) || 1);
        t = Math.round(t / sp) * sp;
      }
      let out = { dx: t * ux, dy: t * uy };
      if (snapEnabled && !opts.noSnap) {
        out = { dx: snapScalar(out.dx, snapMeters), dy: snapScalar(out.dy, snapMeters) };
      }
      return out;
    }

    // free mode
    if (snapEnabled && !opts.noSnap) {
      const ringStep = Math.max(1, Number(ringStepMeters) || 0);
      const radialStepSafe = Math.max(1, Number(radialStep) || 0);
      if (ringStep >= 1 && radialStepSafe >= 1) {
        const r = Math.hypot(dx, dy);
        const az = azimuthMilsFromVector(dx, dy);
        const r2 = snapScalar(r, ringStep);
        const az2 = snapScalar(az, radialStepSafe);
        const a2 = milsToRad(az2);
        return { dx: r2 * Math.sin(a2), dy: r2 * Math.cos(a2) };
      }
      return { dx: snapScalar(dx, snapMeters), dy: snapScalar(dy, snapMeters) };
    }
    return { dx, dy };
  };

  // ── Auto-fit: pick a pixels-per-meter that contains every relevant point ────
  const fitPxPerMeter = useMemo(() => {
    let maxR = 200; // baseline so an empty preview still has scale
    if (mode === "circle") {
      maxR = Math.max(maxR, Number(radiusMeters) || 0);
    }
    for (const g of guns || []) {
      const id = String(g.gun_number);
      const p = points?.[id] || { dx: 0, dy: 0 };
      const c = enabled ? applyConstraint(p.dx, p.dy) : p;
      maxR = Math.max(maxR, Math.hypot(c.dx, c.dy));
    }
    if (mode === "linear") {
      const sp = Math.max(0, Number(spacingMeters) || 0);
      const N = (guns || []).length;
      const halfExtent = ((N - 1) / 2) * sp;
      maxR = Math.max(maxR, halfExtent || 0, 200);
    }
    // pad a bit so labels and markers don't kiss the edge
    const padded = maxR * 1.15 + 50;
    return (centerPx - PADDING_PX) / padded;
  }, [centerPx, enabled, guns, mode, points, radiusMeters, spacingMeters]);

  // ── Coordinate conversions ──────────────────────────────────────────────────
  const metersToPx = (dx, dy) => ({
    x: centerPx + dx * fitPxPerMeter,
    y: centerPx - dy * fitPxPerMeter,
  });
  const pxToMeters = (x, y) => ({
    dx: (x - centerPx) / fitPxPerMeter,
    dy: (centerPx - y) / fitPxPerMeter,
  });

  // ── Drag state ──────────────────────────────────────────────────────────────
  const svgRef = useRef(null);
  const [draggingGunId, setDraggingGunId] = useState(null);
  const [hover, setHover] = useState(null); // { gunId, dx, dy, r, az }

  const getLocal = (clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const sx = rect.width > 0 ? size / rect.width : 1;
    const sy = rect.height > 0 ? size / rect.height : 1;
    return {
      x: (clientX - rect.left) * sx,
      y: (clientY - rect.top) * sy,
    };
  };

  // ── Handlers per gun marker ─────────────────────────────────────────────────
  const handleGunPointerDown = (evt, gunId) => {
    if (!enabled) return;
    evt.stopPropagation();
    evt.preventDefault();
    setDraggingGunId(gunId);
    try { evt.currentTarget.setPointerCapture(evt.pointerId); } catch { /* ignore */ }
  };

  const handleGunPointerMove = (evt, gunId) => {
    if (!enabled) return;
    if (draggingGunId !== gunId) return;
    evt.stopPropagation();
    evt.preventDefault();
    const { x, y } = getLocal(evt.clientX, evt.clientY);
    const raw = pxToMeters(x, y);
    const constrained = applyConstraint(raw.dx, raw.dy, { noSnap: evt.shiftKey });
    setPoints((prev) => ({ ...(prev || {}), [gunId]: constrained }));
    setHover({
      gunId,
      dx: constrained.dx,
      dy: constrained.dy,
      r: Math.hypot(constrained.dx, constrained.dy),
      az: azimuthMilsFromVector(constrained.dx, constrained.dy),
    });
  };

  const handleGunPointerUp = (evt, gunId) => {
    if (draggingGunId === gunId) {
      try { evt.currentTarget.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
      setDraggingGunId(null);
    }
  };

  // Reset every gun back to the target (dx=0, dy=0).
  const resetAll = () => {
    if (!enabled) return;
    setPoints({});
    setHover(null);
  };

  // ── Grid (rings + radials) ─────────────────────────────────────────────────
  const ringStepSafe = Math.max(10, Number(ringStepMeters) || 50);
  const radialMilsSafe = Math.max(50, Number(radialStep) || 200);
  const viewMaxRadiusMeters = (centerPx - PADDING_PX) / fitPxPerMeter;

  const rings = useMemo(() => {
    const out = [];
    const major = ringStepSafe * 4;
    const count = Math.floor(viewMaxRadiusMeters / ringStepSafe);
    for (let i = 1; i <= count; i++) {
      const rm = i * ringStepSafe;
      const isMajor = rm % major === 0;
      out.push(
        <circle
          key={`ring-${rm}`}
          cx={centerPx}
          cy={centerPx}
          r={rm * fitPxPerMeter}
          fill="none"
          stroke={isMajor ? colors.ringMajor : colors.ringMinor}
          strokeWidth={isMajor ? 1.4 : 0.8}
          shapeRendering="geometricPrecision"
        />,
      );
      if (showLabels && isMajor) {
        out.push(
          <text
            key={`rl-${rm}`}
            x={centerPx + 4}
            y={centerPx - rm * fitPxPerMeter - 2}
            fontSize={isMobile ? "10" : "11"}
            fill={colors.hudSubText}
            style={{ pointerEvents: "none" }}
          >
            {rm}m
          </text>,
        );
      }
    }
    return out;
  }, [centerPx, fitPxPerMeter, ringStepSafe, viewMaxRadiusMeters, isMobile, showLabels, colors]);

  const radials = useMemo(() => {
    const out = [];
    const major = radialMilsSafe * 4;
    const count = Math.floor(6400 / radialMilsSafe);
    for (let i = 0; i < count; i++) {
      const mil = i * radialMilsSafe;
      const a = milsToRad(mil);
      const ux = Math.sin(a);
      const uy = Math.cos(a);
      const x2 = centerPx + ux * viewMaxRadiusMeters * fitPxPerMeter;
      const y2 = centerPx - uy * viewMaxRadiusMeters * fitPxPerMeter;
      const isMajor = mil % major === 0;
      out.push(
        <line
          key={`rad-${mil}`}
          x1={centerPx}
          y1={centerPx}
          x2={x2}
          y2={y2}
          stroke={isMajor ? colors.radialMajor : colors.radialMinor}
          strokeWidth={isMajor ? 1.2 : 0.8}
        />,
      );
      if (showLabels && isMajor) {
        const lx = centerPx + ux * (viewMaxRadiusMeters * fitPxPerMeter - 14);
        const ly = centerPx - uy * (viewMaxRadiusMeters * fitPxPerMeter - 14);
        out.push(
          <text
            key={`radl-${mil}`}
            x={lx}
            y={ly}
            fontSize={isMobile ? "9" : "10"}
            fill={colors.hudSubText}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ pointerEvents: "none" }}
          >
            {String(mil).padStart(4, "0")}
          </text>,
        );
      }
    }
    return out;
  }, [centerPx, fitPxPerMeter, radialMilsSafe, viewMaxRadiusMeters, isMobile, showLabels, colors]);

  // ── Pattern overlay (circle outline / linear axis) ──────────────────────────
  const overlay = useMemo(() => {
    if (!enabled) return null;
    if (mode === "circle") {
      const R = Number(radiusMeters) || 0;
      return (
        <circle
          cx={centerPx}
          cy={centerPx}
          r={R * fitPxPerMeter}
          fill="none"
          stroke={colors.overlay}
          strokeDasharray="6 5"
          strokeWidth="1.4"
        />
      );
    }
    if (mode === "linear") {
      const a = milsToRad(Number(bearingMils) || 0);
      const ux = Math.sin(a);
      const uy = Math.cos(a);
      const len = viewMaxRadiusMeters * fitPxPerMeter;
      return (
        <line
          x1={centerPx + ux * len}
          y1={centerPx - uy * len}
          x2={centerPx - ux * len}
          y2={centerPx + uy * len}
          stroke={colors.overlay}
          strokeDasharray="6 5"
          strokeWidth="1.4"
        />
      );
    }
    return null;
  }, [enabled, mode, radiusMeters, bearingMils, centerPx, fitPxPerMeter, viewMaxRadiusMeters, colors.overlay]);

  // Hit radius for the gun marker — bigger on touch so fingers can grab it.
  const HIT_R = isMobile ? 26 : 22;
  const X_HALF = 8;

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%", maxWidth: "100%" }}>
      {/* Top-right control strip */}
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 5 }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltRoundedIcon fontSize="small" />}
          onClick={resetAll}
          disabled={!enabled}
          sx={(t) => ({
            minHeight: 32,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 1,
            backgroundColor: t.palette.background.paper,
            "&:hover": { backgroundColor: t.palette.action.hover },
          })}
        >
          Reset
        </Button>
      </Stack>

      <svg
        ref={svgRef}
        width="100%"
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: "block",
          width: "100%",
          maxWidth: "100%",
          height: size,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          background: colors.bg,
          touchAction: "none",
          userSelect: "none",
          boxSizing: "border-box",
        }}
      >
        {rings}
        {radials}
        {overlay}

        {/* Target marker */}
        <circle cx={centerPx} cy={centerPx} r={5} fill={colors.target} />
        <text
          x={centerPx + 9}
          y={centerPx - 9}
          fontSize={isMobile ? "11" : "12"}
          fill={colors.targetLabel}
          style={{ pointerEvents: "none" }}
        >
          Target
        </text>

        {/* Gun markers — each handles its own pointer events. */}
        {(guns || []).map((g) => {
          const gunId = String(g.gun_number);
          const stored = points?.[gunId] || { dx: 0, dy: 0 };
          const pos = enabled ? applyConstraint(stored.dx, stored.dy) : stored;
          const px = metersToPx(pos.dx, pos.dy);
          const r = Math.hypot(pos.dx, pos.dy);
          const az = azimuthMilsFromVector(pos.dx, pos.dy);
          const isDragging = draggingGunId === gunId;

          return (
            <g key={gunId} transform={`translate(${px.x} ${px.y})`}>
              {/* Hit / drag area — large transparent disc captures pointer. */}
              <circle
                r={HIT_R}
                fill={isDragging ? "rgba(245,158,11,0.18)" : "transparent"}
                stroke={isDragging ? colors.gunSelected : "transparent"}
                strokeWidth={isDragging ? 2 : 0}
                style={{
                  cursor: enabled ? (isDragging ? "grabbing" : "grab") : "not-allowed",
                  touchAction: "none",
                }}
                onPointerDown={(e) => handleGunPointerDown(e, gunId)}
                onPointerMove={(e) => handleGunPointerMove(e, gunId)}
                onPointerUp={(e) => handleGunPointerUp(e, gunId)}
                onPointerCancel={(e) => handleGunPointerUp(e, gunId)}
              />
              {/* Visual cross */}
              <line x1={-X_HALF} y1={-X_HALF} x2={X_HALF} y2={X_HALF} stroke={isDragging ? colors.gunSelected : colors.gun} strokeWidth="2.5" pointerEvents="none" />
              <line x1={-X_HALF} y1={X_HALF} x2={X_HALF} y2={-X_HALF} stroke={isDragging ? colors.gunSelected : colors.gun} strokeWidth="2.5" pointerEvents="none" />
              {/* Gun label */}
              <text x={11} y={-9} fontSize={isMobile ? "11" : "12"} fontWeight={600} fill={colors.gunLabel} style={{ pointerEvents: "none" }}>
                Gun {gunId}
              </text>
              <text x={11} y={6} fontSize={isMobile ? "10" : "11"} fill={colors.gunSubLabel} style={{ pointerEvents: "none", fontVariantNumeric: "tabular-nums" }}>
                {Math.round(r)}m · {String(Math.round(az)).padStart(4, "0")} mil
              </text>
            </g>
          );
        })}
      </svg>

      {/* Bottom-left HUD readout for the active drag */}
      <Box
        sx={(t) => ({
          position: "absolute",
          left: 8,
          bottom: 8,
          px: 1, py: 0.5,
          borderRadius: 1,
          border: `1px solid ${alphaHex(t.palette.divider, 0.6)}`,
          background: alphaHex(t.palette.background.paper, isDark ? 0.85 : 0.92),
          backdropFilter: "blur(4px)",
          fontSize: 11,
          fontWeight: 600,
          color: hover ? t.palette.text.primary : t.palette.text.secondary,
          fontVariantNumeric: "tabular-nums",
          minWidth: 180,
          opacity: enabled ? 1 : 0.6,
          pointerEvents: "none",
        })}
      >
        {hover ? (
          <>
            <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: "warning.main", textTransform: "uppercase", display: "block" }}>
              Gun {hover.gunId}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12 }}>
              {Math.round(hover.r)} m · {String(Math.round(hover.az)).padStart(4, "0")} mil · ΔE {Math.round(hover.dx)}, ΔN {Math.round(hover.dy)}
            </Typography>
          </>
        ) : (
          <Typography component="span" sx={{ fontSize: 11 }}>
            {enabled ? "Drag a gun marker to set its offset" : "Pattern disabled"}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

// Tiny rgba helper that accepts MUI palette colors in any format.
function alphaHex(hexOrColor, a) {
  // For divider/paper colors that come in already as rgba, just use opacity-on-rect.
  // Simpler: return MUI alpha-equivalent by trusting caller to wrap in alpha() upstream.
  return hexOrColor;
}

export default PatternPreview;
